import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Zap, Loader2, Info, Plus, Send, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PlatformCreditBalance {
  ai: { balanceCents: number };
  lead: { grantBalance: number; purchasedBalance: number; total: number };
  isSuperAdmin: boolean;
  pendingRequestCount: number;
  unreadNotificationCount: number;
}

interface CreditRequest {
  id: string;
  creditType: "ai" | "lead";
  amount: number;
  reason: string | null;
  status: "pending" | "approved" | "denied";
  reviewerNote: string | null;
  autoApproved: boolean;
  seenByRequester: boolean;
  createdAt: string;
  resolvedAt: string | null;
  requesterName?: string;
  requesterId?: string;
  requesterEmail?: string;
  autoApproveEnabled?: boolean;
}

// Platform AI credits are stored as dollar-value cents (1 credit = $0.10 = 10¢).
// Display them as true credits by dividing by 10.
function formatCr(cents: number) {
  return Math.floor(cents / 10).toLocaleString() + " cr";
}

// ── Award form (super admin only) ─────────────────────────────────────────────
function AwardForm({ onDone }: { onDone: () => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [creditType, setCreditType] = useState<"ai" | "lead">("ai");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const awardMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/platform/me/credits/award", {
        creditType,
        // AI credits are stored as dollar-value cents (1 credit = 10¢); lead credits are 1:1.
        amount: creditType === "ai" ? parseInt(amount) * 10 : parseInt(amount),
        note: note || undefined,
      }).then((r) => r.json()),
    onSuccess: () => {
      toast({ title: "Credits awarded", description: `${parseInt(amount).toLocaleString()} ${creditType === "ai" ? "AI" : "lead"} credits added to your pool.` });
      qc.invalidateQueries({ queryKey: ["/api/platform/me/credit-balance"] });
      setAmount("");
      setNote("");
      onDone();
    },
    onError: (e: any) =>
      toast({ title: "Failed to award credits", description: e?.message, variant: "destructive" }),
  });

  return (
    <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
      <p className="text-sm font-semibold">Add Credits to Your Pool</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Type</Label>
          <Select value={creditType} onValueChange={(v) => setCreditType(v as "ai" | "lead")}>
            <SelectTrigger data-testid="select-award-credit-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ai"><span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3" />AI Credits</span></SelectItem>
              <SelectItem value="lead"><span className="flex items-center gap-1.5"><Zap className="w-3 h-3" />Lead Credits</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Amount</Label>
          <Input
            type="number"
            min="1"
            step="1"
            placeholder="100"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="input-award-amount"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Note (optional)</Label>
        <Input
          placeholder="Reason…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="input-award-note"
        />
      </div>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={!amount || parseInt(amount) <= 0 || awardMutation.isPending}
          onClick={() => awardMutation.mutate()}
          data-testid="button-award-credits-submit"
        >
          {awardMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {awardMutation.isPending ? "Adding…" : "Add Credits"}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone} data-testid="button-award-cancel">Cancel</Button>
      </div>
    </div>
  );
}

// ── Request form (non-admin) ───────────────────────────────────────────────────
function RequestForm({ onDone }: { onDone: (status: string) => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [creditType, setCreditType] = useState<"ai" | "lead">("ai");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const requestMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/platform/credit-requests", {
        creditType,
        // AI credits are stored as dollar-value cents (1 credit = 10¢); lead credits are 1:1.
        amount: creditType === "ai" ? parseInt(amount) * 10 : parseInt(amount),
        reason: reason || undefined,
      }).then((r) => r.json()),
    onSuccess: (data) => {
      if (data.autoApproved) {
        toast({ title: "Credits approved!", description: `${parseInt(amount).toLocaleString()} ${creditType === "ai" ? "AI" : "lead"} credits added to your pool immediately.` });
      } else {
        toast({ title: "Request submitted", description: "Your credit request is pending super admin approval." });
      }
      qc.invalidateQueries({ queryKey: ["/api/platform/me/credit-balance"] });
      qc.invalidateQueries({ queryKey: ["/api/platform/credit-requests/my"] });
      setAmount("");
      setReason("");
      onDone(data.status);
    },
    onError: (e: any) =>
      toast({ title: "Failed to submit request", description: e?.message, variant: "destructive" }),
  });

  return (
    <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
      <p className="text-sm font-semibold">Request Credits</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Type</Label>
          <Select value={creditType} onValueChange={(v) => setCreditType(v as "ai" | "lead")}>
            <SelectTrigger data-testid="select-request-credit-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ai"><span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3" />AI Credits</span></SelectItem>
              <SelectItem value="lead"><span className="flex items-center gap-1.5"><Zap className="w-3 h-3" />Lead Credits</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Amount</Label>
          <Input
            type="number"
            min="1"
            step="1"
            placeholder="100"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="input-request-amount"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Reason (optional)</Label>
        <Textarea
          placeholder="Why do you need these credits?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="resize-none text-sm"
          rows={2}
          data-testid="textarea-request-reason"
        />
      </div>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={!amount || parseInt(amount) <= 0 || requestMutation.isPending}
          onClick={() => requestMutation.mutate()}
          data-testid="button-request-credits-submit"
        >
          {requestMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          {requestMutation.isPending ? "Submitting…" : "Submit Request"}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDone("")} data-testid="button-request-cancel">Cancel</Button>
      </div>
    </div>
  );
}

// ── My requests panel (non-admin) ─────────────────────────────────────────────
function MyRequestsPanel() {
  const qc = useQueryClient();
  const { data: requests, isLoading } = useQuery<CreditRequest[]>({
    queryKey: ["/api/platform/credit-requests/my"],
    queryFn: () => apiRequest("GET", "/api/platform/credit-requests/my").then((r) => r.json()),
  });

  const seenMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/platform/credit-requests/seen").then((r) => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/me/credit-balance"] });
      qc.invalidateQueries({ queryKey: ["/api/platform/credit-requests/my"] });
    },
  });

  if (isLoading) return <div className="text-sm text-muted-foreground py-2 text-center">Loading…</div>;
  if (!requests?.length) return <div className="text-sm text-muted-foreground py-2 text-center">No requests yet.</div>;

  const hasUnread = requests.some((r) => r.status !== "pending" && !r.seenByRequester);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">My Requests</p>
        {hasUnread && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs px-2"
            onClick={() => seenMutation.mutate()}
            data-testid="button-mark-requests-seen"
          >
            Mark all seen
          </Button>
        )}
      </div>
      <div className="rounded-lg border divide-y max-h-52 overflow-y-auto">
        {requests.map((r) => (
          <div key={r.id} className="flex items-start justify-between px-3 py-2.5 gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                {r.creditType === "ai" ? <Sparkles className="w-3 h-3 shrink-0 text-primary" /> : <Zap className="w-3 h-3 shrink-0 text-amber-500" />}
                <span className="text-sm font-medium">{Math.floor(r.creditType === "ai" ? r.amount / 10 : r.amount).toLocaleString()} {r.creditType === "ai" ? "AI" : "lead"} cr</span>
                <Badge
                  variant={r.status === "approved" ? "default" : r.status === "denied" ? "destructive" : "secondary"}
                  className="text-xs"
                  data-testid={`badge-request-status-${r.id}`}
                >
                  {r.autoApproved ? "auto-approved" : r.status}
                </Badge>
                {r.status !== "pending" && !r.seenByRequester && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                )}
              </div>
              {r.reason && <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.reason}</p>}
              {r.reviewerNote && <p className="text-xs text-muted-foreground mt-0.5 italic">"{r.reviewerNote}"</p>}
            </div>
            <span className="text-xs text-muted-foreground shrink-0">{new Date(r.createdAt).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Pending requests panel (super admin) ──────────────────────────────────────
function PendingRequestsPanel({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showResolved, setShowResolved] = useState(false);
  const [denyNotes, setDenyNotes] = useState<Record<string, string>>({});
  const [denyingId, setDenyingId] = useState<string | null>(null);

  const { data: requests, isLoading } = useQuery<CreditRequest[]>({
    queryKey: ["/api/platform/credit-requests", showResolved ? "all" : "pending"],
    queryFn: () =>
      apiRequest("GET", `/api/platform/credit-requests?status=${showResolved ? "all" : "pending"}`).then((r) => r.json()),
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, action, reviewerNote }: { id: string; action: "approve" | "deny"; reviewerNote?: string }) =>
      apiRequest("PATCH", `/api/platform/credit-requests/${id}`, { action, reviewerNote }).then((r) => r.json()),
    onSuccess: (_d, { action, id }) => {
      toast({ title: action === "approve" ? "Request approved" : "Request denied", description: action === "approve" ? "Credits have been added to their pool." : "Requester has been notified." });
      setDenyingId(null);
      setDenyNotes((prev) => { const n = { ...prev }; delete n[id]; return n; });
      qc.invalidateQueries({ queryKey: ["/api/platform/credit-requests"] });
      qc.invalidateQueries({ queryKey: ["/api/platform/me/credit-balance"] });
    },
    onError: (e: any) =>
      toast({ title: "Action failed", description: e?.message, variant: "destructive" }),
  });

  const autoApproveMutation = useMutation({
    mutationFn: ({ platformUserId, enabled }: { platformUserId: string; enabled: boolean }) =>
      apiRequest("POST", "/api/platform/credit-requests/auto-approve", { platformUserId, enabled }).then((r) => r.json()),
    onSuccess: (_d, { enabled }) => {
      toast({ title: enabled ? "Auto-approve enabled" : "Auto-approve disabled", description: enabled ? "Future requests from this user will be approved immediately." : "Requests will go through the normal approval queue." });
      qc.invalidateQueries({ queryKey: ["/api/platform/credit-requests"] });
    },
    onError: (e: any) =>
      toast({ title: "Failed to update auto-approve", description: e?.message, variant: "destructive" }),
  });

  const pending = requests?.filter((r) => r.status === "pending") ?? [];

  if (isLoading) return <div className="text-sm text-muted-foreground py-2 text-center">Loading…</div>;
  if (!requests?.length && !showResolved)
    return (
      <div className="text-center py-3 space-y-1.5">
        <p className="text-sm text-muted-foreground">No pending requests.</p>
        <button className="text-xs text-primary underline underline-offset-2" onClick={() => setShowResolved(true)}>View resolved</button>
      </div>
    );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
          Pending Requests
          {pending.length > 0 && <Badge variant="destructive" className="text-xs h-4 px-1.5">{pending.length}</Badge>}
        </p>
        <button
          className="text-xs text-primary underline underline-offset-2"
          onClick={() => setShowResolved((v) => !v)}
          data-testid="button-toggle-show-resolved"
        >
          {showResolved ? "Hide resolved" : "Show resolved"}
        </button>
      </div>

      {requests?.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-2">No requests found.</p>
      ) : (
        <div className="rounded-lg border divide-y max-h-72 overflow-y-auto">
          {(requests ?? []).map((r) => (
            <div key={r.id} className="px-3 py-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-medium">{r.requesterName ?? r.requesterEmail}</span>
                    <Badge variant="secondary" className="text-xs">{r.requesterEmail}</Badge>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {r.creditType === "ai" ? <Sparkles className="w-3 h-3 text-primary" /> : <Zap className="w-3 h-3 text-amber-500" />}
                    <span className="text-sm">{Math.floor(r.creditType === "ai" ? r.amount / 10 : r.amount).toLocaleString()} {r.creditType === "ai" ? "AI" : "lead"} cr</span>
                    <Badge
                      variant={r.status === "approved" ? "default" : r.status === "denied" ? "destructive" : "secondary"}
                      className="text-xs"
                    >
                      {r.status}
                    </Badge>
                  </div>
                  {r.reason && <p className="text-xs text-muted-foreground mt-0.5">"{r.reason}"</p>}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{new Date(r.createdAt).toLocaleDateString()}</span>
              </div>

              {r.status === "pending" && (
                <>
                  {denyingId === r.id ? (
                    <div className="space-y-2">
                      <Input
                        placeholder="Denial note (optional)…"
                        value={denyNotes[r.id] ?? ""}
                        onChange={(e) => setDenyNotes((prev) => ({ ...prev, [r.id]: e.target.value }))}
                        className="text-sm h-8"
                        data-testid={`input-deny-note-${r.id}`}
                      />
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="destructive"
                          className="flex-1 h-7 text-xs"
                          disabled={reviewMutation.isPending}
                          onClick={() => reviewMutation.mutate({ id: r.id, action: "deny", reviewerNote: denyNotes[r.id] || undefined })}
                          data-testid={`button-deny-confirm-${r.id}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Confirm Deny
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => setDenyingId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-1.5 items-center">
                      <Button
                        size="sm"
                        className="flex-1 h-7 text-xs"
                        disabled={reviewMutation.isPending}
                        onClick={() => reviewMutation.mutate({ id: r.id, action: "approve" })}
                        data-testid={`button-approve-request-${r.id}`}
                      >
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-7 text-xs"
                        disabled={reviewMutation.isPending}
                        onClick={() => setDenyingId(r.id)}
                        data-testid={`button-deny-request-${r.id}`}
                      >
                        <X className="w-3 h-3 mr-1" /> Deny
                      </Button>
                    </div>
                  )}
                </>
              )}

              {r.requesterId && (
                <div className="flex items-center justify-between pt-0.5">
                  <Label className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1.5">
                    <Switch
                      checked={r.autoApproveEnabled ?? false}
                      onCheckedChange={(checked) =>
                        autoApproveMutation.mutate({ platformUserId: r.requesterId!, enabled: checked })
                      }
                      className="scale-75 origin-left"
                      data-testid={`switch-auto-approve-${r.requesterId}`}
                    />
                    Auto-approve this user
                  </Label>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── BalanceDialog ─────────────────────────────────────────────────────────────
function BalanceDialog({
  open,
  onOpenChange,
  data,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  data: PlatformCreditBalance;
}) {
  const [showAwardForm, setShowAwardForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showPendingPanel, setShowPendingPanel] = useState(data.pendingRequestCount > 0);
  const [showMyRequests, setShowMyRequests] = useState(data.unreadNotificationCount > 0);

  const handleClose = (v: boolean) => {
    if (!v) {
      setShowAwardForm(false);
      setShowRequestForm(false);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="dialog-platform-credits">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Platform Credit Pools
          </DialogTitle>
          <DialogDescription>
            These are the platform's own credit balances — separate from any tenant workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* ── AI Credits balance ─────────────────────────────────────── */}
          <div className="rounded-lg border border-border px-4 py-3 space-y-1.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">AI Credits</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm">Balance</span>
              </div>
              <span className="text-sm font-semibold" data-testid="text-platform-ai-balance">
                {formatCr(data.ai.balanceCents)}
              </span>
            </div>
          </div>

          {/* ── Lead Credits balance ───────────────────────────────────── */}
          <div className="rounded-lg border border-border px-4 py-3 space-y-1.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Lead Credits</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm">Grant</span>
              </div>
              <span className="text-sm font-semibold" data-testid="text-platform-lead-grant">
                {data.lead.grantBalance.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-sm">Purchased</span>
              </div>
              <span className="text-sm font-semibold" data-testid="text-platform-lead-purchased">
                {data.lead.purchasedBalance.toLocaleString()}
              </span>
            </div>
            <div className="border-t border-border pt-1.5 flex items-center justify-between">
              <span className="text-sm font-medium">Total</span>
              <span className="text-sm font-bold" data-testid="text-platform-lead-total">
                {data.lead.total.toLocaleString()}
              </span>
            </div>
          </div>

          {/* ── Super admin: award form ────────────────────────────────── */}
          {data.isSuperAdmin && !showAwardForm && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowAwardForm(true)}
              data-testid="button-show-award-form"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Credits
            </Button>
          )}
          {data.isSuperAdmin && showAwardForm && (
            <AwardForm onDone={() => setShowAwardForm(false)} />
          )}

          {/* ── Non-admin: request form ────────────────────────────────── */}
          {!data.isSuperAdmin && !showRequestForm && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowRequestForm(true)}
              data-testid="button-show-request-form"
            >
              <Send className="w-4 h-4 mr-2" /> Request Credits
              {data.unreadNotificationCount > 0 && (
                <Badge variant="destructive" className="ml-2 h-4 px-1 text-xs">{data.unreadNotificationCount}</Badge>
              )}
            </Button>
          )}
          {!data.isSuperAdmin && showRequestForm && (
            <RequestForm onDone={() => setShowRequestForm(false)} />
          )}

          {/* ── Non-admin: my requests history ─────────────────────────── */}
          {!data.isSuperAdmin && (
            <>
              <button
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowMyRequests((v) => !v)}
                data-testid="button-toggle-my-requests"
              >
                {showMyRequests ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                My request history
                {data.unreadNotificationCount > 0 && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                )}
              </button>
              {showMyRequests && <MyRequestsPanel />}
            </>
          )}

          {/* ── Super admin: pending requests panel ────────────────────── */}
          {data.isSuperAdmin && (
            <>
              <Separator />
              <button
                className="flex items-center gap-1.5 text-xs font-medium hover:text-foreground transition-colors w-full text-left"
                onClick={() => setShowPendingPanel((v) => !v)}
                data-testid="button-toggle-pending-requests"
              >
                {showPendingPanel ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <span>Credit Requests</span>
                {data.pendingRequestCount > 0 && (
                  <Badge variant="destructive" className="h-4 px-1.5 text-xs ml-1">{data.pendingRequestCount}</Badge>
                )}
              </button>
              {showPendingPanel && <PendingRequestsPanel />}
            </>
          )}
        </div>

        {data.isSuperAdmin && (
          <p className="text-xs text-muted-foreground text-center pb-1 flex items-center justify-center gap-1">
            <Info className="w-3 h-3" />
            Credits awarded here go to your own platform pool.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main pill component ───────────────────────────────────────────────────────
export function PlatformCreditPill() {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: bal, isLoading } = useQuery<PlatformCreditBalance>({
    queryKey: ["/api/platform/me/credit-balance"],
    refetchInterval: 60_000,
  });

  if (isLoading || !bal) {
    return (
      <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted/50 animate-pulse h-7 w-24" />
    );
  }

  const aiBalance = bal.ai?.balanceCents ?? 0;
  const leadTotal = bal.lead?.total ?? 0;
  const low = aiBalance < 200;
  const badgeCount = (bal.pendingRequestCount ?? 0) + (bal.unreadNotificationCount ?? 0);

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setDialogOpen(true)}
          title="Platform credit pools — click for breakdown"
          data-testid="button-platform-credit-pill"
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors shrink-0",
            "border cursor-pointer",
            low
              ? "border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
              : "border-primary/20 bg-primary/8 text-primary dark:bg-primary/10",
            "hover:border-primary/50 hover:bg-primary/15",
          )}
        >
          <Sparkles className={cn("w-3 h-3 shrink-0", low && "text-red-500 dark:text-red-400")} />
          <span data-testid="text-platform-ai-credit-balance">{formatCr(aiBalance)}</span>
          <span className="text-muted-foreground/70">·</span>
          <Zap className="w-3 h-3 shrink-0 text-amber-500" />
          <span data-testid="text-platform-lead-credit-balance">{leadTotal.toLocaleString()}</span>
        </button>
        {badgeCount > 0 && (
          <span
            className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1"
            data-testid="badge-platform-credit-pill-count"
          >
            {badgeCount}
          </span>
        )}
      </div>

      {bal && (
        <BalanceDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          data={bal}
        />
      )}
    </>
  );
}
